import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const MOBILE_QUERY = '(max-width: 749px)';

/**
 * A mobile "pick your size" bottom sheet.
 *
 * On mobile the theme hides the in-flow add-to-cart button, so the sticky bar is
 * the only way to add to cart. This component:
 *   1. Keeps Shopify's default variant selection on load.
 *   2. Opens the size sheet only if an option is genuinely unselected
 *      (see the interception in `sticky-add-to-cart.js`).
 *   3. Proxies the sheet's size buttons to the real `<variant-picker>` radios, so
 *      the theme's native variant logic drives everything (price, the hidden
 *      variant id, the sticky bar). Nothing about the variant flow is re-invented.
 *
 * Desktop is untouched — the feature only engages on mobile.
 *
 * @extends {Component}
 */
class SizePickerDrawer extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    // Keep the sheet in sync whether the size is chosen in the sheet or on the page.
    section?.addEventListener(ThemeEvents.variantSelected, this.#sync, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, this.#sync, { signal });

    requestAnimationFrame(this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  /** @returns {HTMLElement | null} The product's main variant picker. */
  #variantPicker() {
    const id = this.dataset.productId;
    return (
      document.querySelector(`variant-picker[ref="mainVariantPicker"][data-product-id="${id}"]`) ||
      document.querySelector('variant-picker[ref="mainVariantPicker"]')
    );
  }

  /** @returns {HTMLElement | null} The product form for this product. */
  #productForm() {
    return document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  /** @returns {HTMLButtonElement | null} The hidden main add-to-cart button. */
  #mainAddButton() {
    return /** @type {HTMLButtonElement | null} */ (
      this.#productForm()?.querySelector('[ref="addToCartButton"]') ?? null
    );
  }

  /** @returns {HTMLElement | null} The nested dialog-component that runs the sheet. */
  #dialog() {
    return this.querySelector('dialog-component');
  }

  /** @returns {HTMLButtonElement | null} */
  #confirmButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.size-picker-drawer__confirm'));
  }

  /** @returns {HTMLButtonElement[]} */
  #sizeButtons() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('.size-picker-drawer__size')));
  }

  /** @returns {HTMLFieldSetElement[]} The option groups in the main variant picker. */
  #fieldsets() {
    const picker = this.#variantPicker();
    return /** @type {HTMLFieldSetElement[]} */ (picker ? Array.from(picker.querySelectorAll('fieldset')) : []);
  }

  /**
   * Whether the shopper still needs to pick an option before adding to cart.
   * Read by `sticky-add-to-cart.js` to decide whether to open the sheet.
   * @returns {boolean}
   */
  requiresSelection() {
    if (!this.#isMobile()) return false;
    const fieldsets = this.#fieldsets();
    if (!fieldsets.length) return false;
    return fieldsets.some((fieldset) => !fieldset.querySelector('input:checked'));
  }

  /** Opens the bottom sheet. Called by the sticky add-to-cart button. */
  open() {
    if (!this.#isMobile()) return;
    this.#sync();
    this.#dialog()?.showDialog();
  }

  /**
   * Proxies a size tap in the sheet to the real variant picker radio, letting the
   * theme's native change flow update price, variant id and the sticky bar.
   * @param {Event} event
   */
  selectOption(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('.size-picker-drawer__size');
    if (!(button instanceof HTMLElement) || button.dataset.available === 'false') return;

    const radio = this.#variantPicker()?.querySelector(
      `input[data-option-value-id="${button.dataset.optionValueId}"]`
    );
    if (radio instanceof HTMLInputElement) radio.click();

    this.#sync();
  }

  /** Adds the now-selected variant to cart, reusing the sticky bar's add flow. */
  confirm() {
    if (this.requiresSelection() || this.#mainAddButton()?.disabled) return;

    this.#dialog()?.closeDialog();

    const sticky = /** @type {any} */ (
      document.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`)
    );
    if (sticky && typeof sticky.handleAddToCartClick === 'function') {
      sticky.handleAddToCartClick();
      return;
    }

    // Fallback: submit through the hidden main add-to-cart button.
    const button = this.#mainAddButton();
    if (button) {
      button.dataset.puppet = 'true';
      button.click();
    }
  }

  /** Reflects the picker's current selection into the sheet UI. */
  #sync = () => {
    const picker = this.#variantPicker();
    if (!picker) return;

    const selected = new Set(
      Array.from(picker.querySelectorAll('input:checked')).map(
        (input) => /** @type {HTMLElement} */ (input).dataset.optionValueId
      )
    );

    for (const button of this.#sizeButtons()) {
      button.setAttribute('aria-pressed', selected.has(button.dataset.optionValueId) ? 'true' : 'false');
    }

    const confirmButton = this.#confirmButton();
    if (confirmButton) confirmButton.disabled = this.requiresSelection() || Boolean(this.#mainAddButton()?.disabled);
  };
}

if (!customElements.get('size-picker-drawer')) {
  customElements.define('size-picker-drawer', SizePickerDrawer);
}
